import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Activity, RefreshCw, Server, Database, CheckCircle, XCircle, AlertTriangle, Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

const SystemHealthPage = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/health`);
      setHealth(response.data);
    } catch (error) {
      console.error("Failed to fetch health status:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const handleClearErrors = async () => {
    setClearing(true);
    try {
      await axios.delete(`${API}/health/clear-errors`);
      fetchHealth();
    } catch (error) {
      console.error("Failed to clear errors:", error);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="health-page">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">System Health</h2>
          <p className="text-muted-foreground">Monitor API and database status</p>
        </div>
        <Button onClick={fetchHealth} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* API Status */}
        <Card data-testid="api-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Server className="w-4 h-4" />
              API Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-12 w-full" />
            ) : (
              <div className="flex items-center gap-3">
                {health?.apiStatus?.status === 'healthy' ? (
                  <CheckCircle className="w-8 h-8 text-green-500" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-500" />
                )}
                <div>
                  <p className="font-semibold capitalize">{health?.apiStatus?.status}</p>
                  <p className="text-xs text-muted-foreground">{health?.apiStatus?.responseTimeMs}ms response</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Database Status */}
        <Card data-testid="db-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Database className="w-4 h-4" />
              Database
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-12 w-full" />
            ) : (
              <div className="flex items-center gap-3">
                {health?.database?.status === 'connected' ? (
                  <CheckCircle className="w-8 h-8 text-green-500" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-500" />
                )}
                <div>
                  <p className="font-semibold capitalize">{health?.database?.status}</p>
                  <p className="text-xs text-muted-foreground">MongoDB</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Last Content Sync */}
        <Card data-testid="content-sync-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Content Sync
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-12 w-full" />
            ) : (
              <div>
                <p className="font-semibold">
                  {health?.lastContentSync?.timestamp 
                    ? new Date(health.lastContentSync.timestamp).toLocaleString() 
                    : 'Never'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {health?.lastContentSync?.recordsCount || 0} records
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Sync Activity */}
        <Card data-testid="user-sync-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="w-4 h-4" />
              User Sync (1hr)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-12 w-full" />
            ) : (
              <div>
                <p className="text-2xl font-bold">{health?.lastUserSync?.syncRequestsLastHour || 0}</p>
                <p className="text-xs text-muted-foreground">sync requests</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Collections Sizes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Collection Sizes
          </CardTitle>
          <CardDescription>Document counts per MongoDB collection</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[100px] w-full" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {health?.database?.collections && Object.entries(health.database.collections).map(([name, count]) => (
                <div key={name} className="p-3 rounded-lg bg-muted text-center">
                  <p className="text-xl font-bold">{count.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground truncate">{name}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Logs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Error Log
            </CardTitle>
            <CardDescription>Last 10 API errors</CardDescription>
          </div>
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={handleClearErrors}
            disabled={clearing || !health?.errorLogs?.length}
            data-testid="clear-errors-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Log
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : health?.errorLogs?.length > 0 ? (
            <div className="space-y-2">
              {health.errorLogs.map((error, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200"
                  data-testid={`error-log-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="destructive">{error.statusCode}</Badge>
                    <span className="font-mono text-sm">{error.endpoint}</span>
                    <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {error.message}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(error.timestamp).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
              <p>No errors logged</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealthPage;
